import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, unref as _unref, resolveDynamicComponent as _resolveDynamicComponent, withModifiers as _withModifiers, withCtx as _withCtx, createBlock as _createBlock, resolveComponent as _resolveComponent, createVNode as _createVNode, Fragment as _Fragment, normalizeStyle as _normalizeStyle, renderList as _renderList } from "vue";
const _hoisted_1 = {
    key: 0,
    class: "lane__head"
};
const _hoisted_2 = ["title"];
const _hoisted_3 = {
    key: 0,
    class: "lane__subtitle--mini"
};
const _hoisted_4 = ["title"];
const _hoisted_5 = { class: "lane__now-step" };
const _hoisted_6 = {
    key: 1,
    class: "lane__subject"
};
const _hoisted_7 = {
    key: 0,
    class: "lane__subtitle"
};
const _hoisted_8 = {
    key: 1,
    class: "lane__appeal"
};
const _hoisted_9 = ["aria-label"];
const _hoisted_10 = {
    class: "lane__mark",
    "aria-hidden": "true"
};
const _hoisted_11 = {
    key: 0,
    class: "lane__short"
};
const _hoisted_12 = { class: "lane__label" };
const _hoisted_13 = { class: "lane__label-long" };
const _hoisted_14 = { class: "lane__label-short" };
const _hoisted_15 = { class: "lane__meta" };
const _hoisted_16 = {
    key: 0,
    class: "lane__date"
};
const _hoisted_17 = {
    key: 1,
    class: "lane__holder"
};
const _hoisted_18 = ["aria-label", "onMouseenter"];
const _hoisted_19 = {
    class: "lane__mark",
    "aria-hidden": "true"
};
const _hoisted_20 = {
    key: 0,
    class: "lane__short"
};
const _hoisted_21 = { class: "lane__label" };
const _hoisted_22 = { class: "lane__label-long" };
const _hoisted_23 = { class: "lane__label-short" };
const _hoisted_24 = { class: "lane__meta" };
const _hoisted_25 = { class: "lane__tip-title" };
const _hoisted_26 = { class: "lane__tip-line" };
import { computed, onBeforeUnmount, ref } from "vue";
import { RouterLink } from "vue-router";
import { GHOST_TEXT, ghostOf, sharedColumns, shortDate, waitingText, } from "@/models/missionControl";
import { caseModule } from "@/models/caseModules";
import { STATE_LABELS } from "@/models/worklistRow";
const DRAW_MS = 220;
const TIP_OPEN_MS = 60;
const TIP_CLOSE_MS = 80;
export default /*@__PURE__*/ _defineComponent({
    __name: 'JourneyLane',
    props: {
        lane: {},
        mini: { type: Boolean, default: false },
        columns: { default: () => [] },
        pipeline: { default: () => [] },
        delay: { default: 0 }
    },
    setup(__props) {
        const props = __props;
        const titleText = computed(() => [props.lane.title, props.lane.subtitle].filter(Boolean).join(" · "));
        const nowNode = computed(() => props.lane.current ?? [...props.lane.nodes].reverse().find((node) => !node.hidden) ?? null);
        const keys = computed(() => {
            const own = props.lane.nodes.map((node) => node.moduleKey);
            const shared = props.columns.length ? props.columns : sharedColumns([props.lane], props.pipeline);
            return [...shared, ...own.filter((key) => !shared.includes(key))];
        });
        function ghostAt(key) {
            const kind = ghostOf(key, props.lane, props.pipeline);
            return kind ? { ...caseModule(key), text: GHOST_TEXT[kind] } : null;
        }
        const positions = computed(() => keys.value.map((key, index) => {
            const node = [...props.lane.nodes].reverse().find((item) => item.moduleKey === key) ?? null;
            return { key, index, node, ghost: node ? null : ghostAt(key) };
        }));
        const placed = computed(() => positions.value.filter((slot) => slot.node));
        const doneRange = computed(() => {
            const list = placed.value;
            if (list.length < 2)
                return null;
            const currentAt = list.findIndex((slot) => slot.node === props.lane.current);
            const end = currentAt === -1 ? list.length - 1 : currentAt;
            return end > 0 ? { from: list[0].index, to: list[end].index } : null;
        });
        function popDelay(node, index) {
            if (!node || node.hidden)
                return null;
            const range = doneRange.value;
            if (node === props.lane.current)
                return props.delay + (range ? DRAW_MS : 0);
            if (node.state !== "DONE")
                return null;
            if (!range)
                return props.delay;
            const along = Math.min(Math.max((index - range.from) / (range.to - range.from), 0), 1);
            return props.delay + Math.round(along * DRAW_MS);
        }
        const cells = computed(() => positions.value.map((slot) => ({ ...slot, pop: popDelay(slot.node, slot.index) })));
        const gridStyle = computed(() => ({ "--columns": String(Math.max(keys.value.length, 1)) }));
        function span(from, to) {
            const width = 100 / Math.max(keys.value.length, 1);
            return { left: `${(from + 0.5) * width}%`, width: `${Math.max(to - from, 0) * width}%` };
        }
        const railStyle = computed(() => {
            const last = [...positions.value].reverse().find((slot) => slot.node || slot.ghost);
            return last && last.index > 0 ? span(0, last.index) : { display: "none" };
        });
        const doneStyle = computed(() => doneRange.value ? span(doneRange.value.from, doneRange.value.to) : { display: "none" });
        function metaOf(node) {
            if (node.hidden)
                return "Not enabled";
            if (node === props.lane.current)
                return node.text;
            if (node.state === "DONE")
                return node.text && node.text !== "Done" ? node.text : "Done";
            return node.text;
        }
        function dateOf(node) {
            if (node.hidden || !node.at)
                return "";
            if (node === props.lane.current || (node.isOpen && node.tone === "attention"))
                return waitingText(node.at);
            return node.isOpen ? "" : shortDate(node.at);
        }
        function stateOf(node) {
            return node.hidden ? node.text : node.text || STATE_LABELS[node.state];
        }
        function describe(node) {
            const when = node.at ? `, ${shortDate(node.at)}` : "";
            return `${node.label}: ${stateOf(node)}${when}`;
        }
        function tipLine(node) {
            return [stateOf(node), node.hidden ? "" : shortDate(node.at)].filter(Boolean).join(" · ");
        }
        const tip = ref(null);
        const tipOpen = ref(false);
        let tipTimer;
        function showTip(title, line, event) {
            clearTimeout(tipTimer);
            const host = event.currentTarget;
            const open = () => {
                tip.value = { title, line, el: host.querySelector(".lane__mark") ?? host };
                tipOpen.value = true;
            };
            if (tipOpen.value || event.type === "focus")
                open();
            else
                tipTimer = setTimeout(open, TIP_OPEN_MS);
        }
        function hideTip() {
            clearTimeout(tipTimer);
            tipTimer = setTimeout(() => (tipOpen.value = false), TIP_CLOSE_MS);
        }
        onBeforeUnmount(() => clearTimeout(tipTimer));
        return (_ctx, _cache) => {
            const _component_v_icon = _resolveComponent("v-icon");
            const _component_v_tooltip = _resolveComponent("v-tooltip");
            return (_openBlock(), _createElementBlock("div", {
                class: _normalizeClass(["lane", __props.mini ? 'lane--mini' : { 'lane--closed': !__props.lane.current }]),
                style: _normalizeStyle({ '--lane-delay': `${__props.delay}ms` })
            }, [
                (__props.mini)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                        _createElementVNode("span", {
                            class: "lane__title lane__title--mini",
                            title: titleText.value
                        }, [
                            _createTextVNode(_toDisplayString(__props.lane.title), 1),
                            (__props.lane.subtitle)
                                ? (_openBlock(), _createElementBlock("span", _hoisted_3, " · " + _toDisplayString(__props.lane.subtitle), 1))
                                : _createCommentVNode("", true)
                        ], 8, _hoisted_2),
                        (nowNode.value)
                            ? (_openBlock(), _createElementBlock("span", {
                                key: 0,
                                class: _normalizeClass(["lane__now", `lane__now--${nowNode.value.tone}`]),
                                title: describe(nowNode.value)
                            }, [
                                _createElementVNode("span", _hoisted_5, _toDisplayString(nowNode.value.label) + " · ", 1),
                                _createTextVNode(_toDisplayString(nowNode.value.text), 1)
                            ], 10, _hoisted_4))
                            : _createCommentVNode("", true)
                    ]))
                    : (_openBlock(), _createElementBlock("div", _hoisted_6, [
                        (_openBlock(), _createBlock(_resolveDynamicComponent(__props.lane.to ? _unref(RouterLink) : 'span'), {
                            to: __props.lane.to ?? undefined,
                            class: "lane__title",
                            onClick: _cache[0] || (_cache[0] = _withModifiers(() => { }, ["stop"]))
                        }, {
                            default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(__props.lane.title), 1)
                            ]),
                            _: 1
                        }, 8, ["to"])),
                        (__props.lane.subtitle)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_7, _toDisplayString(__props.lane.subtitle), 1))
                            : _createCommentVNode("", true),
                        (__props.lane.appeal)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_8, [
                                _createVNode(_component_v_icon, {
                                    icon: "mdi-gavel",
                                    size: "12",
                                    "aria-hidden": "true"
                                }),
                                (__props.lane.appeal.sentAt)
                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                        _createTextVNode(" Appeal sent " + _toDisplayString(_unref(shortDate)(__props.lane.appeal.sentAt)), 1),
                                        (__props.lane.appeal.sentBy)
                                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                                _createTextVNode(" by " + _toDisplayString(__props.lane.appeal.sentBy), 1)
                                            ], 64))
                                            : _createCommentVNode("", true)
                                    ], 64))
                                    : (__props.lane.appeal.draftedAt)
                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                            _createTextVNode("Appeal drafted " + _toDisplayString(_unref(shortDate)(__props.lane.appeal.draftedAt)), 1)
                                        ], 64))
                                        : _createCommentVNode("", true)
                            ]))
                            : _createCommentVNode("", true)
                    ])),
                _createElementVNode("div", {
                    class: _normalizeClass(["lane__grid", { 'lane__grid--mini': __props.mini }]),
                    role: "list",
                    style: _normalizeStyle(gridStyle.value),
                    "aria-label": `Journey: ${__props.lane.title}`
                }, [
                    _createElementVNode("span", {
                        class: "lane__rail",
                        style: _normalizeStyle(railStyle.value),
                        "aria-hidden": "true"
                    }, null, 4),
                    _createElementVNode("span", {
                        class: "lane__rail lane__rail--done",
                        style: _normalizeStyle(doneStyle.value),
                        "aria-hidden": "true"
                    }, null, 4),
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(cells.value, (column) => {
                        return (_openBlock(), _createElementBlock("div", {
                            key: column.key,
                            class: "lane__cell",
                            role: "listitem"
                        }, [
                            (column.node)
                                ? (_openBlock(), _createBlock(_resolveDynamicComponent(column.node.to ? _unref(RouterLink) : 'span'), {
                                    key: 0,
                                    to: column.node.to ?? undefined,
                                    class: _normalizeClass(["lane__node", [
                                            __props.mini ? 'lane__node--mini' : 'lane__node--full',
                                            `lane__step--${column.node.tone}`,
                                            {
                                                'lane__step--current': column.node === __props.lane.current,
                                                'lane__step--hidden': column.node.hidden,
                                                'lane__node--pop': column.pop !== null,
                                            },
                                        ]]),
                                    style: _normalizeStyle(column.pop === null ? undefined : { '--node-delay': `${column.pop}ms` }),
                                    "aria-label": describe(column.node),
                                    onMouseenter: ($event) => (showTip(column.node.label, tipLine(column.node), $event)),
                                    onMouseleave: hideTip,
                                    onFocus: ($event) => (showTip(column.node.label, tipLine(column.node), $event)),
                                    onBlur: hideTip,
                                    onClick: _cache[1] || (_cache[1] = _withModifiers(() => { }, ["stop"]))
                                }, {
                                    default: _withCtx(() => [
                                        _createElementVNode("span", _hoisted_10, [
                                            _createVNode(_component_v_icon, {
                                                icon: column.node.icon,
                                                size: __props.mini ? 15 : 17
                                            }, null, 8, ["icon", "size"])
                                        ]),
                                        (__props.mini)
                                            ? (_openBlock(), _createElementBlock("span", _hoisted_11, _toDisplayString(column.node.short), 1))
                                            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                                _createElementVNode("span", _hoisted_12, [
                                                    _createElementVNode("span", _hoisted_13, _toDisplayString(column.node.label), 1),
                                                    _createElementVNode("span", _hoisted_14, _toDisplayString(column.node.short), 1)
                                                ]),
                                                _createElementVNode("span", _hoisted_15, _toDisplayString(metaOf(column.node)), 1),
                                                (dateOf(column.node))
                                                    ? (_openBlock(), _createElementBlock("span", _hoisted_16, _toDisplayString(dateOf(column.node)), 1))
                                                    : _createCommentVNode("", true),
                                                (column.node === __props.lane.current && __props.lane.holders)
                                                    ? (_openBlock(), _createElementBlock("span", _hoisted_17, _toDisplayString(__props.lane.holders), 1))
                                                    : _createCommentVNode("", true)
                                            ], 64))
                                    ]),
                                    _: 2
                                }, 1064, ["to", "class", "style", "aria-label", "onMouseenter", "onFocus"]))
                                : (column.ghost)
                                    ? (_openBlock(), _createElementBlock("span", {
                                        key: 1,
                                        class: _normalizeClass(["lane__node lane__step--ghost", __props.mini ? 'lane__node--mini' : 'lane__node--full']),
                                        "aria-label": `${column.ghost.label}: ${column.ghost.text}`,
                                        onMouseenter: ($event) => (showTip(column.ghost.label, column.ghost.text, $event)),
                                        onMouseleave: hideTip
                                    }, [
                                        _createElementVNode("span", _hoisted_19, [
                                            _createVNode(_component_v_icon, {
                                                icon: column.ghost.icon,
                                                size: __props.mini ? 15 : 17
                                            }, null, 8, ["icon", "size"])
                                        ]),
                                        (__props.mini)
                                            ? (_openBlock(), _createElementBlock("span", _hoisted_20, _toDisplayString(column.ghost.short), 1))
                                            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                                _createElementVNode("span", _hoisted_21, [
                                                    _createElementVNode("span", _hoisted_22, _toDisplayString(column.ghost.label), 1),
                                                    _createElementVNode("span", _hoisted_23, _toDisplayString(column.ghost.short), 1)
                                                ]),
                                                _createElementVNode("span", _hoisted_24, _toDisplayString(column.ghost.text), 1)
                                            ], 64))
                                    ], 42, _hoisted_18))
                                    : _createCommentVNode("", true)
                        ]));
                    }), 128))
                ], 14, _hoisted_9),
                (tip.value)
                    ? (_openBlock(), _createBlock(_component_v_tooltip, {
                        key: 2,
                        modelValue: tipOpen.value,
                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((tipOpen).value = $event)),
                        target: tip.value.el,
                        location: "top",
                        offset: 6
                    }, {
                        default: _withCtx(() => [
                            _createElementVNode("span", _hoisted_25, _toDisplayString(tip.value.title), 1),
                            _createElementVNode("span", _hoisted_26, _toDisplayString(tip.value.line), 1)
                        ]),
                        _: 1
                    }, 8, ["modelValue", "target"]))
                    : _createCommentVNode("", true)
            ], 6));
        };
    }
});
